<template>
  <el-drawer
    :visible="dialogVisible"
    direction="rtl"
    :size="drawerSize"
    destroy-on-close
    :before-close="handleDrawerBeforeClose"
    custom-class="product-currency-drawer"
    :with-header="false"
  >
    <div class="detail-workspace" v-loading="loading">
      <div class="breadcrumb-header">
        <div class="header-top-row">
          <div class="header-left">
            <span class="header-title">
              <span class="header-title-icon"><i class="el-icon-s-goods"></i></span>
              商品挡位配置
            </span>
            <span class="header-subtitle">{{ productData.id }} - {{ productData.name || '' }}</span>
          </div>

          <div class="header-right-buttons">
            <el-button size="small" @click="handleCloseCurrencyDialog"><i class="el-icon-back"></i> 返回列表</el-button>
            <el-button type="success" size="small" @click="handleSaveDialog">保存</el-button>
          </div>
        </div>
      </div>

      <div class="detail-content box">
        <div class="form-section currency-section">
          <div class="panel-section-header">
            <div class="panel-title-icon"><i class="el-icon-money"></i></div>
            <div>
              <h3 class="panel-section-title">货币配置</h3>
              <p class="section-subtitle">默认货币不可删除，可新增其他货币并分别维护商品挡位。</p>
            </div>
          </div>

          <div class="currency-card-grid">
            <div
              v-for="item in currencyConfigs"
              :key="item.currency"
              class="currency-card"
              :class="{ active: item.currency === activeCurrency }"
              @click="handleSelectCurrency(item.currency)"
            >
              <div class="currency-card-main">
                <div class="currency-code">{{ item.currency }}</div>
                <div class="currency-name">{{ getCurrencyLabel(item.currency) }}</div>
              </div>
              <div class="currency-meta">
                <el-tag v-if="item.isDefault" size="mini" type="success">默认</el-tag>
                <span>{{ getDisplayProducts(item.products).length }} 个挡位</span>
              </div>
              <el-button
                v-if="!item.isDefault"
                size="mini"
                type="text"
                icon="el-icon-delete"
                class="currency-delete"
                @click.stop="handleRemoveCurrency(item.currency)"
              ></el-button>
            </div>

            <div class="currency-add-card">
              <el-select v-model="newCurrency" size="small" placeholder="新增货币" style="width: 132px">
                <el-option
                  v-for="item in availableCurrencyOptions"
                  :key="item.value"
                  :label="item.value + ' - ' + item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
              <el-button type="primary" size="small" icon="el-icon-plus" class="btn-primary-action" @click="handleAddCurrency">新增</el-button>
            </div>
          </div>
        </div>

        <div class="form-section product-section">
          <div class="panel-section-header">
            <div class="panel-title-icon"><i class="el-icon-tickets"></i></div>
            <div>
              <h3 class="panel-section-title">商品挡位明细</h3>
              <p class="section-subtitle">当前货币：{{ activeCurrency }} - {{ getCurrencyLabel(activeCurrency) }}</p>
            </div>
          </div>

          <div class="table-container">
            <el-table :data="products" stripe style="width: 100%" class="premium-table inline-edit-table">
              <el-table-column label="商品 ID" min-width="130">
                <template slot-scope="scope">
                  <el-input v-model="scope.row.id" :disabled="isProductIdLocked(scope.row)" placeholder="商品ID" size="small"></el-input>
                </template>
              </el-table-column>

              <el-table-column label="价格（分）" width="130">
                <template slot-scope="scope">
                  <el-input v-model.number="scope.row.price" placeholder="价格" size="small" type="number"></el-input>
                </template>
              </el-table-column>

              <el-table-column label="商品名称" min-width="150">
                <template slot-scope="scope">
                  <el-input v-model="scope.row.name" placeholder="商品名称" size="small"></el-input>
                </template>
              </el-table-column>

              <el-table-column label="商品描述" min-width="220">
                <template slot-scope="scope">
                  <el-input v-model="scope.row.desc" placeholder="商品描述" size="small"></el-input>
                </template>
              </el-table-column>

              <el-table-column label="操作" width="76" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-button type="danger" size="mini" circle icon="el-icon-delete" plain :disabled="isCatalogLocked" @click="handleDeleteParams(scope.$index)"></el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="toolbar-row">
            <el-button type="primary" size="small" icon="el-icon-plus" class="btn-primary-action" :disabled="isCatalogLocked" @click="handleAddParams">添加单行</el-button>
            <el-button size="small" icon="el-icon-download" plain @click="handleExportTemplate">导出 Excel 模板</el-button>
            <el-upload action="" :auto-upload="false" :show-file-list="false" accept=".xlsx,.xls,.csv" :on-change="handleAddFile" class="upload-inline">
              <el-button size="small" icon="el-icon-upload2" plain>导入 Excel</el-button>
            </el-upload>
          </div>
        </div>

        <div class="form-section json-section">
          <div class="panel-section-header compact-header">
            <div class="panel-title-icon"><i class="el-icon-document"></i></div>
            <div>
              <h3 class="panel-section-title">查看/编辑 JSON</h3>
              <p class="section-subtitle">默认展示当前商品挡位 JSON，可切换到编辑模式后批量应用。</p>
            </div>
            <el-radio-group v-model="jsonMode" size="mini" class="json-mode-switch" @change="handleJsonModeChange">
              <el-radio-button label="view">查看</el-radio-button>
              <el-radio-button label="edit">编辑</el-radio-button>
            </el-radio-group>
          </div>

          <pre v-if="jsonMode === 'view'" class="json-preview">{{ formattedProductsJson }}</pre>
          <div v-else class="json-editor">
            <el-input
              v-model="textarea"
              type="textarea"
              :rows="8"
              placeholder='请输入 JSON 数组，例如：[{"id":"101","price":600,"name":"6元礼包","desc":"测试描述"}]'
              class="code-textarea"
            ></el-input>
            <div class="section-actions">
              <el-button type="success" size="small" icon="el-icon-check" @click="handleApplyJson">应用 JSON</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import options from '@/common/options'
import api from '@/api/channel'

const emptyProduct = () => ({ id: '', name: '', price: 0, desc: '' })

export default {
  name: 'currencyDialog',
  props: {
    dialogVisible: Boolean,
    productData: Object
  },
  data () {
    return {
      viewportWidth: window.innerWidth,
      loading: false,
      options: options.currency,
      defaultCurrency: 'CNY',
      activeCurrency: 'CNY',
      newCurrency: '',
      currencyConfigs: [],
      products: [emptyProduct()],
      textarea: '',
      jsonMode: 'view'
    }
  },
  computed: {
    appId () {
      return this.$store.getters.appId
    },
    drawerSize () {
      if (this.viewportWidth <= 768) return '100%'
      if (this.viewportWidth <= 1200) return '72%'
      return '50%'
    },
    availableCurrencyOptions () {
      const used = this.currencyConfigs.map(item => item.currency)
      return this.options.filter(item => !used.includes(item.value))
    },
    formattedProductsJson () {
      return JSON.stringify(this.getPersistProducts(this.products), null, 2)
    },
    isCatalogLocked () {
      return this.activeCurrency !== this.defaultCurrency
    }
  },
  watch: {
    productData: {
      handler (val) {
        if (val && val.id) {
          this.initCurrencyConfigs(val)
        }
      },
      immediate: true
    },
    products: {
      handler () {
        this.cacheActiveProducts()
        if (this.jsonMode === 'view') {
          this.textarea = this.formattedProductsJson
        }
      },
      deep: true
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.viewportWidth = window.innerWidth
    },
    initCurrencyConfigs (product) {
      this.loading = true
      this.defaultCurrency = product.currency || 'CNY'
      this.activeCurrency = this.defaultCurrency
      const defaultProducts = Array.isArray(product.products) ? product.products : []
      this.currencyConfigs = [{
        currency: this.defaultCurrency,
        products: this.cloneProducts(defaultProducts.length ? defaultProducts : [emptyProduct()]),
        isDefault: true,
        loaded: true
      }]
      this.products = this.cloneProducts(this.currencyConfigs[0].products)
      this.textarea = this.formattedProductsJson
      this.jsonMode = 'view'

      const tasks = this.options
        .filter(item => item.value !== this.defaultCurrency)
        .map(item => this.fetchCurrencyProducts(item.value).then(products => {
          if (this.getDisplayProducts(products).length) {
            this.currencyConfigs.push({
              currency: item.value,
              products: this.cloneProducts(products, true),
              isDefault: false,
              loaded: true
            })
          }
        }).catch(() => {}))

      Promise.all(tasks).finally(() => {
        this.loading = false
      })
    },
    fetchCurrencyProducts (currency) {
      const params = { appId: this.productData.id, currency }
      return api.getCurrencyData(params).then(res => {
        if (res && res.products) {
          return JSON.parse(res.products)
        }
        return []
      })
    },
    cloneProducts (products, lockProductId = false) {
      const list = Array.isArray(products) ? products : []
      return list.length
        ? list.map(item => {
          return {
            id: item.id,
            name: item.name,
            price: item.price,
            desc: item.desc,
            idLocked: lockProductId || !!item.idLocked
          }
        })
        : [emptyProduct()]
    },
    getDisplayProducts (products) {
      return (products || []).filter(item => item && item.id !== undefined && String(item.id).trim() !== '')
    },
    getPersistProducts (products) {
      return this.getDisplayProducts(products).map(item => {
        return {
          id: item.id,
          name: item.name,
          price: item.price,
          desc: item.desc
        }
      })
    },
    isProductIdLocked (row) {
      return this.isCatalogLocked || !!(row && row.idLocked)
    },
    getCurrencyLabel (currency) {
      const item = this.options.find(option => option.value === currency)
      return item ? item.label : currency
    },
    cacheActiveProducts () {
      const config = this.currencyConfigs.find(item => item.currency === this.activeCurrency)
      if (config) {
        config.products = this.cloneProducts(this.products)
      }
    },
    handleSelectCurrency (currency) {
      if (currency === this.activeCurrency) return
      this.cacheActiveProducts()
      this.activeCurrency = currency
      const config = this.currencyConfigs.find(item => item.currency === currency)
      this.products = this.cloneProducts(config ? config.products : [])
      this.jsonMode = 'view'
      this.textarea = this.formattedProductsJson
    },
    handleAddCurrency () {
      if (!this.newCurrency) {
        return this.$message.warning('请选择需要新增的货币类型')
      }
      this.cacheActiveProducts()
      const defaultConfig = this.currencyConfigs.find(item => item.currency === this.defaultCurrency)
      const defaultProducts = defaultConfig ? this.getPersistProducts(defaultConfig.products) : []
      this.currencyConfigs.push({
        currency: this.newCurrency,
        products: defaultProducts.length ? this.cloneProducts(defaultProducts, true) : [emptyProduct()],
        isDefault: false,
        loaded: true
      })
      this.handleSelectCurrency(this.newCurrency)
      this.newCurrency = ''
    },
    handleRemoveCurrency (currency) {
      const config = this.currencyConfigs.find(item => item.currency === currency)
      if (config && config.isDefault) {
        return this.$message.warning('默认货币不可删除')
      }
      this.currencyConfigs = this.currencyConfigs.filter(item => item.currency !== currency)
      if (this.activeCurrency === currency) {
        this.activeCurrency = this.defaultCurrency
        const defaultConfig = this.currencyConfigs.find(item => item.currency === this.defaultCurrency)
        this.products = this.cloneProducts(defaultConfig ? defaultConfig.products : [])
      }
    },
    handleAddParams () {
      if (this.isCatalogLocked) {
        return this.$message.warning('非默认货币使用默认商品目录，不支持新增商品')
      }
      this.products.push(emptyProduct())
    },
    handleDeleteParams (i) {
      if (this.isCatalogLocked) {
        return this.$message.warning('非默认货币使用默认商品目录，不支持删除商品')
      }
      this.products.splice(i, 1)
      if (!this.products.length) {
        this.products.push(emptyProduct())
      }
    },
    handleSaveDialog () {
      this.cacheActiveProducts()
      const tasks = this.currencyConfigs.map(config => {
        const products = this.getPersistProducts(config.products)
        const params = {
          appId: this.productData.id,
          currency: config.currency,
          products: products.length ? JSON.stringify(products) : null
        }
        return api.addOrChangeCurrency(params)
      })
      Promise.all(tasks).then(() => {
        this.$message.success('保存成功')
        this.$emit('handleSaveCurrencyDialog', false)
      })
    },
    handleCloseCurrencyDialog () {
      this.$emit('handleCloseCurrencyDialog', false)
    },
    handleDrawerBeforeClose (done) {
      this.handleCloseCurrencyDialog()
      done()
    },
    handleJsonModeChange (mode) {
      if (mode === 'edit') {
        this.textarea = this.formattedProductsJson
      }
    },
    handleApplyJson () {
      if (!this.textarea) {
        return this.$message.info('请输入内容')
      }
      if (!this.isJsonString(this.textarea)) {
        return this.$message.info('请输入正确的 JSON 数组格式')
      }
      const parsed = JSON.parse(this.textarea)
      if (!Array.isArray(parsed)) {
        return this.$message.info('JSON 内容必须是数组')
      }
      if (this.isCatalogLocked) {
        const inputMap = new Map(parsed.filter(item => item && item.id !== undefined).map(item => [String(item.id), item]))
        this.products = this.products.map(item => {
          const next = inputMap.get(String(item.id))
          return next
            ? Object.assign({}, item, {
              name: next.name,
              price: next.price,
              desc: next.desc,
              idLocked: true
            })
            : item
        })
      } else {
        this.products = parsed.length ? this.cloneProducts(parsed) : [emptyProduct()]
      }
      this.jsonMode = 'view'
      this.$message.success('JSON 已应用')
    },
    handleExportTemplate () {
      const XLSX = require('xlsx')
      const worksheet = XLSX.utils.json_to_sheet([
        { id: '商品ID', price: '价格（分）', name: '商品名称', desc: '商品描述' },
        { id: '101', price: 600, name: '6元礼包', desc: '示例描述' }
      ], { skipHeader: true })
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '商品挡位模板')
      XLSX.writeFile(workbook, `商品挡位模板-${this.activeCurrency}.xlsx`)
    },
    handleAddFile (uploadFile) {
      if (!uploadFile || !uploadFile.raw) {
        return this.$message.warning('请选择文件')
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target.result
          const XLSX = require('xlsx')
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          const excelData = XLSX.utils.sheet_to_json(worksheet, {
            header: ['id', 'price', 'name', 'desc'],
            range: 1
          })
          if (excelData.length === 0) {
            return this.$message.warning('Excel 文件中没有数据')
          }

          excelData.forEach(row => {
            if (row.id !== undefined && row.id !== null) {
              row.id = String(row.id).trim()
            }
            if (row.price !== undefined && row.price !== null) {
              row.price = Number(row.price)
            }
          })

          const oldMap = new Map(this.getDisplayProducts(this.products).map(item => [item.id, item]))
          for (const newItem of excelData) {
            if (!newItem.id) continue
            newItem.idLocked = this.activeCurrency !== this.defaultCurrency
            if (oldMap.has(newItem.id)) {
              Object.assign(oldMap.get(newItem.id), newItem)
            } else if (!this.isCatalogLocked) {
              oldMap.set(newItem.id, newItem)
            }
          }

          this.products = Array.from(oldMap.values())
          if (!this.products.length) {
            this.products = [emptyProduct()]
          }
          this.$message.success('导入成功')
        } catch (error) {
          console.error('解析 Excel 文件失败:', error)
          this.$message.error('解析 Excel 文件失败')
        }
      }
      reader.onerror = () => {
        this.$message.error('读取文件失败')
      }
      reader.readAsArrayBuffer(uploadFile.raw)
    },
    isJsonString (str) {
      try {
        JSON.parse(str)
        return true
      } catch (e) {
        return false
      }
    }
  }
}
</script>

<style scoped>
.detail-workspace {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  overflow: hidden;
}

.breadcrumb-header {
  position: sticky;
  top: 0;
  z-index: 100;
  margin: 0;
  padding: 12px 20px;
  box-sizing: border-box;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.header-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}

.header-title-icon {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  background: #eff6ff;
  border-radius: 8px;
}

.header-subtitle {
  font-size: 12.5px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}

.header-right-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.detail-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 24px 28px;
  background: #ffffff;
}

.box {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.form-section {
  padding: 18px 0 20px;
  border-bottom: 1px solid #e2e8f0;
}

.form-section:first-child {
  padding-top: 0;
}

.form-section:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.panel-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.compact-header {
  align-items: flex-start;
}

.panel-title-icon {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  background: #eff6ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.panel-section-title {
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  color: #1e293b;
}

.section-subtitle {
  margin: 2px 0 0;
  font-size: 12.5px;
  line-height: 18px;
  color: #64748b;
}

.currency-card-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.currency-card,
.currency-add-card {
  min-height: 86px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px 14px;
  box-sizing: border-box;
}

.currency-card {
  position: relative;
  width: 164px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.currency-card:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.currency-card.active {
  border-color: rgba(37, 99, 235, 0.55);
  background: #eff6ff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.08);
}

.currency-code {
  color: #1e293b;
  font-size: 18px;
  font-weight: 800;
  line-height: 24px;
}

.currency-name {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.currency-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.currency-delete {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #ef4444;
}

.currency-add-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  margin-bottom: 0;
}

.upload-inline {
  display: inline-block;
}

.btn-primary-action {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-primary-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.table-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0;
}

.premium-table {
  font-size: 13px !important;
  color: #334155 !important;
}

.inline-edit-table :deep(.el-table__header-wrapper) th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  padding: 9px 0 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.inline-edit-table :deep(.el-table__row) td {
  padding: 7px 0 !important;
}

.json-mode-switch {
  margin-left: auto;
  flex-shrink: 0;
}

.json-preview {
  min-height: 120px;
  max-height: 320px;
  overflow: auto;
  margin: 0;
  padding: 14px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.6;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  white-space: pre-wrap;
  word-break: break-word;
}

.code-textarea :deep(.el-textarea__inner) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  background-color: #ffffff;
  border-color: #e2e8f0;
  color: #1e293b;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .breadcrumb-header {
    padding: 12px 16px;
  }

  .header-top-row,
  .header-left {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-right-buttons {
    width: 100%;
    justify-content: flex-end;
  }

  .detail-content {
    padding: 16px;
  }

  .currency-card,
  .currency-add-card {
    width: 100%;
  }

  .currency-add-card {
    justify-content: space-between;
  }

  .compact-header {
    flex-wrap: wrap;
  }

  .json-mode-switch {
    margin-left: 42px;
  }
}
</style>
